import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./HomeJobListings.css";
import { FaMapMarkerAlt, FaBriefcase, FaRupeeSign, FaBookmark, FaRegBookmark, FaArrowRight } from "react-icons/fa";
import { SearchJob, CreateMyJobs } from "../../../Redux/Slices/JobPost";
import { formatTimeAgo } from "../../../Helper/FromatTime";
import toast from "react-hot-toast";

const HomeJobListings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [savedJobs, setSavedJobs] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const jobsPerPage = 12;
  
  const userId = localStorage.getItem("id");
  const role = localStorage.getItem("role");

  useEffect(() => {
    fetchJobs();
  }, [currentPage]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const data = {
        page: currentPage,
        limit: jobsPerPage,
      };
      const response = await SearchJob(data);
      setJobs(response?.docs || []);
      setTotalPages(response?.pagination?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveJob = async (jobId, e) => {
    e.stopPropagation();
    
    if (!userId || role !== "Jobseeker") {
      toast.error("Please login as Job Seeker to save jobs");
      navigate("/signin");
      return;
    }

    try {
      const data = {
        type: "saved",
        jobid: jobId,
      };
      await CreateMyJobs(userId, data);
      
      if (savedJobs.includes(jobId)) {
        setSavedJobs(savedJobs.filter(id => id !== jobId));
        toast.success("Job removed from saved");
      } else {
        setSavedJobs([...savedJobs, jobId]);
        toast.success("Job saved successfully");
      }
    } catch (error) {
      toast.error("Failed to save job");
    }
  };

  const handleJobClick = (jobId) => {
    navigate(`/job-description/${jobId}`);
  };

  const handleViewAllJobs = () => {
    navigate("/jobs");
  };

  // Pagination
  const maxVisiblePages = 5;

  const getPageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <section id="jobs-section" className="home-job-listings">
      <div className="home-job-listings-container">
        {/* Section Header */}
        <div className="home-job-listings-header">
          <div>
            <h2 className="home-job-listings-title">Latest Job Openings</h2>
            <p className="home-job-listings-subtitle">
              Discover the newest opportunities from top companies
            </p>
          </div>
          <button onClick={handleViewAllJobs} className="view-all-jobs-btn">
            View All Jobs <FaArrowRight />
          </button>
        </div>
        <div className="home-job-listings-sitelinks">
          <Link to="/ca-jobs">CA Jobs</Link>
          <Link to="/cs-jobs">CS Jobs</Link>
          <Link to="/cma-jobs">CMA Jobs</Link>
          <Link to="/featured-job">Featured Jobs</Link>
          <Link to="/career-sites">PSU Jobs</Link>
          <Link to="/post-job">Post a Job</Link>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="job-cards-grid">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="job-card-skeleton">
                <div className="skeleton-header"></div>
                <div className="skeleton-line"></div>
                <div className="skeleton-line short"></div>
                <div className="skeleton-footer"></div>
              </div>
            ))}
          </div>
        )}

        {/* Job Cards Grid */}
        {!loading && jobs.length > 0 && (
          <>
            <div className="job-cards-grid">
              {jobs
                .filter((job) => job?.admin_approval === "approved" && job?.is_active)
                .map((job) => (
                  <div
                    key={job._id}
                    className="job-card"
                    onClick={() => handleJobClick(job._id)}
                  >
                    {/* Save Button */}
                    <button
                      className="job-card-save-btn"
                      onClick={(e) => handleSaveJob(job._id, e)}
                      aria-label="Save job"
                    >
                      {savedJobs.includes(job._id) ? (
                        <FaBookmark className="saved" />
                      ) : (
                        <FaRegBookmark />
                      )}
                    </button>

                    {/* Job Card Content */}
                    <div className="job-card-header">
                      <h3 className="job-card-title">{job.title}</h3>
                      <p className="job-card-company">{job.company_name}</p>
                    </div>

                    <div className="job-card-details">
                      {job.exp?.from !== null && job.exp?.from !== undefined && 
                       job.exp?.to !== null && job.exp?.to !== undefined && (
                        <span className="job-card-detail">
                          <FaBriefcase />
                          {job.exp.from}-{job.exp.to} yrs
                        </span>
                      )}
                      
                      {job.ctc && job.ctc.from_lac !== null && job.ctc.from_lac !== undefined && 
                       job.ctc.to_lac !== null && job.ctc.to_lac !== undefined && (
                        <span className="job-card-detail">
                          <FaRupeeSign />
                          {job.ctc.from_lac}-{job.ctc.to_lac} LPA
                        </span>
                      )}
                      
                      {job.cities && job.cities[0] && (
                        <span className="job-card-detail">
                          <FaMapMarkerAlt />
                          {job.cities[0]}
                        </span>
                      )}
                    </div>

                    <div className="job-card-footer">
                      <span className="job-card-posted">
                        {formatTimeAgo(new Date(job.admin_approval_date || job.posted_date))}
                      </span>
                      <span className={`job-card-type ${job.category?.toLowerCase().replace(/\s+/g, '-')}`}>
                        {job.category}
                      </span>
                    </div>
                  </div>
                ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="home-pagination">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn pagination-prev"
                >
                  Previous
                </button>

                <div className="pagination-numbers">
                  {currentPage > 2 && (
                    <>
                      <button onClick={() => setCurrentPage(1)} className="pagination-number">
                        1
                      </button>
                      {currentPage > 3 && <span className="pagination-ellipsis">...</span>}
                    </>
                  )}

                  {getPageNumbers().map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`pagination-number ${currentPage === page ? "active" : ""}`}
                    >
                      {page}
                    </button>
                  ))}

                  {currentPage < totalPages - 1 && (
                    <>
                      {currentPage < totalPages - 2 && <span className="pagination-ellipsis">...</span>}
                      <button onClick={() => setCurrentPage(totalPages)} className="pagination-number">
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>

                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn pagination-next"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!loading && jobs.length === 0 && (
          <div className="jobs-empty-state">
            <div className="empty-state-icon">📋</div>
            <h3>No Jobs Available</h3>
            <p>Check back later for new opportunities</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default HomeJobListings;
